<template>
  <div class="base-card-info">
    <div class="info-card-title">
      {{ title }}
    </div>

    <el-form
      ref="formRef"
      class="flex"
      require-asterisk-position="right"
      label-position="top"
      :model="formValues"
      :rules="rules"
    >
      <div class="w-[432px] mr-[114px]">
        <el-form-item prop="phone" label="验证手机号">
          <el-input
            v-model="formValues.phone"
            clearable
            autocomplete="nony"
            placeholder="请输入"
            @keyup.enter="onKeyEnter"
          />
          <template #label>
            <div class="label">
              验证手机号
              <el-popover
                width="353"
                placement="right"
                trigger="hover"
              >
                <template #reference>
                  <svg-icon icon="mingcute:information-line" />
                </template>
                <div>
                  {{ phoneDesc }}
                </div>
              </el-popover>
            </div>
          </template>
        </el-form-item>
        <el-form-item prop="code" class="relative" label="验证码">
          <el-input
            v-model="formValues.code"
            class="w-[314px] relative box-border pl36"
            clearable
            autocomplete="nony"
            placeholder="请输入"
            @keyup.enter="onKeyEnter"
          >
            <template #prefix>
              <svg-icon class="left-[10px] !w-[18px] z-12 absolute" icon="lucide:shield-check" />
            </template>
          </el-input>

          <sms-button
            ref="smsBtnRef"
            class="relative !left-auto ml-[16px]"
            :disabled="disableSmsBtn"
            @click="doSendSms"
          />
        </el-form-item>

        <template v-if="reValid">
          <el-form-item prop="" label="银行预留手机号">
            <el-input :model-value="reservedPhone" disabled />
            <template #label>
              <div class="label">
                银行预留手机号
                <el-popover
                  width="353"
                  placement="right"
                  trigger="hover"
                >
                  <template #reference>
                    <svg-icon icon="mingcute:information-line" />
                  </template>
                  <div>用于验证法人银行卡</div>
                </el-popover>
              </div>
            </template>
          </el-form-item>
          <el-form-item prop="verifyCode" class="relative" label="验证码">
            <el-input
              v-model="formValues.verifyCode"
              class="w-[314px] relative box-border pl36"
              clearable
              autocomplete="nony"
              placeholder="请输入"
              @keyup.enter="onKeyEnter"
            >
              <template #prefix>
                <svg-icon class="left-[10px] !w-[18px] z-12 absolute" icon="lucide:shield-check" />
              </template>
            </el-input>

            <sms-button
              ref="revalidBtnRef"
              class="relative !left-auto ml-[16px]"
              @click="doRevalid"
            />
          </el-form-item>
        </template>

        <el-form-item prop="password" label="设置支付密码">
          <secure-password
            name="password"
            class="w-[432px]"
            placeholder="请输入支付密码"
            :random-keys="randomKeys"
            @change="onPassChange"
          />
          <template #label>
            <div class="label">
              设置支付密码
              <el-popover
                placement="right"
                trigger="hover"
              >
                <template #reference>
                  <svg-icon icon="mingcute:information-line" />
                </template>
                <div>
                  {{ passwordDesc }}
                </div>
              </el-popover>
            </div>
          </template>
        </el-form-item>
        <el-form-item prop="rePassword" label="确认支付密码">
          <secure-password
            name="rePassword"
            class="w-[432px]"
            placeholder="请输入支付密码"
            :random-keys="randomKeys"
            @change="onRePassChange"
          />
        </el-form-item>
        <el-form-item>
          <span
            class="cursor-pointer flex items-center"
            @click="checkedService = !checkedService"
          >
            <el-checkbox
              class="mr-2"
              :model-value="checkedService"
            />
            <label class="cursor-pointer ml-2 text-[#6B7891]">同意并阅读</label>
            <a
              class="text-[var(--color-primary)] text-[14px] hover:underline"
              @click="(e) => openService(e)"
            >《连连银通银行卡支付服务协议》</a>
          </span>
        </el-form-item>
      </div>
    </el-form>
  </div>
</template>

<script setup lang="ts">
import { ElMessage, FormInstance, FormRules } from 'element-plus';
import { computed, PropType, reactive, ref, toRaw, watch } from 'vue';
import { checkPhone, checkSmsCode } from '@core/tools';
import { IRandomKeys, ISecurePassword, SmsButton } from '@vue3/components';
import { SecurePassword, queryRandomPassKeys, SvgIcon } from '@vue3/components';
import { IApplySubmitParam } from './type';
import { useRoute } from 'vue-router';

let sendSmsLock = false;
let revalidLock = false;

const emit = defineEmits<{submit: [value: IApplySubmitParam]}>();

defineOptions({ name: 'ApplySubmitForm' });

const route = useRoute();

const props = defineProps({
  sendSms: {
    type: Function as PropType<(phone: string) => Promise<void>>,
    default() {
      return () => {};
    },
  },
  revalidFn: {
    type: Function as PropType<(check?: boolean) => Promise<void>>,
    default() {
      return () => {};
    },
  },
  reValid: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: '开通账户验证',
  },
  phone: {
    type: String,
    default: '',
  },
  phoneDesc: {
    type: String,
    default: '连连账户开户验证手机号，余额消费、换绑手机号、提现代发等交易需要验证时，接收短信验证码的手机号',
  },
  passwordDesc: {
    type: String,
    default: '设置连连支付密码',
  },
  randomQueryId: {
    type: [Number, String],
    required: true,
  },
  randomPath: {
    type: String,
    required: true,
  },
  randomApiMethod: {
    type: String as PropType<'get' | 'post'>,
    default: 'get',
  },
});

const disableSmsBtn = computed(() => checkPhone(formValues.value.phone) !== '');
const reservedPhone = computed(() => route.query.reservedPhone as string);

const checkedService = ref(false);

const formRef = ref<FormInstance>();
const smsBtnRef = ref<InstanceType<typeof SmsButton>>(null);
const revalidBtnRef = ref<InstanceType<typeof SmsButton>>(null);

const formValues = ref({} as IApplySubmitParam);

const randomKeys = ref({} as IRandomKeys);
const passObj = ref({} as ISecurePassword);
const rePassObj = ref({} as ISecurePassword);

const rules = reactive<FormRules<IApplySubmitParam>>({
  phone: [
    {
      required: true,
      validator: (rule: any, value: string, callback: any) => {
        if (checkPhone(formValues.value.phone)) {
          callback(checkPhone(formValues.value.phone, '验证'));
        }
        callback();
      },
    },
  ],
  code: [
    {
      required: true,
      validator: (rule: any, value: string, callback: any) => {
        if (checkSmsCode(formValues.value.code)) {
          callback(checkSmsCode(formValues.value.code));
        }
        callback();
      },
    },
  ],
  verifyCode: reservedPhone.value
    ? [
      {
        required: true,
        message: '请输入预留手机号验证码',
      },
    ]
    : undefined,
  password: [
    {
      required: true,
      message: '请输入支付密码',
    },
    {
      required: true,
      validator: (rule: any, value: string, callback: any) => {
        if (passObj.value.hash && passObj.value.length < 6) {
          callback(new Error('支付密码必须是6-20位'));
        }
        callback();
      },
    },
    {
      validator: (rule: any, value: string, callback: any) => {
        if (
          passObj.value.hash
          && rePassObj.value.hash
          && passObj.value.hash !== rePassObj.value.hash
        ) {
          callback(new Error('两次支付密码输入不一致'));
        }
        callback();
      },
    },
  ],
  rePassword: [
    {
      required: true,
      message: '请输入确认支付密码',
    },
    {
      validator: (rule: any, value: string, callback: any) => {
        if (rePassObj.value.hash && rePassObj.value.length < 6) {
          callback(new Error('确认支付密码必须是6-20位'));
        }
        callback();
      },
    },
    {
      validator: (rule: any, value: string, callback: any) => {
        if (
          passObj.value.hash
          && rePassObj.value.hash
          && passObj.value.hash !== rePassObj.value.hash
        ) {
          callback(new Error('两次支付密码输入不一致'));
        }
        callback();
      },
    },
  ],
});

function onKeyEnter() {
  validateForm(() => {
    emit('submit', toRaw(formValues.value));
  });
}

async function doSendSms() {
  if (sendSmsLock) return;

  try {
    sendSmsLock = true;
    smsBtnRef.value?.showLoading();

    await props.sendSms(formValues.value.phone);

    smsBtnRef.value?.countdown();
  } catch (error) {
    smsBtnRef.value?.hideLoading();
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }

  setTimeout(() => {
    sendSmsLock = false;
  }, 1300);
}

async function doRevalid() {
  if (revalidLock) return;

  if (!formValues.value.phone) return formRef.value?.validateField('phone');
  if (!formValues.value.code) return formRef.value?.validateField('code');

  try {
    revalidLock = true;
    revalidBtnRef.value?.showLoading();

    await props.revalidFn(true);

    revalidBtnRef.value?.countdown();
  } catch (error) {
    revalidBtnRef.value?.hideLoading();
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }

  setTimeout(() => {
    revalidLock = false;
  }, 1300);
}

async function validateForm(
  callback?: (values: IApplySubmitParam) => void,
  failCallback?: () => void,
) {
  if (!formRef.value) return;

  // @ts-ignore
  await formRef.value.validate((valid) => {
    if (valid) {
      if (!checkedService.value) return ElMessage.error('请阅读并同意协议');
      callback && callback(toRaw(formValues.value));
    } else {
      failCallback && failCallback();
    }
  });
}

async function fetchRandomKeys() {
  try {
    randomKeys.value = await queryRandomPassKeys({
      propertyValue: Number(props.randomQueryId),
      prefix: props.randomPath,
      apiMethod: props.randomApiMethod || 'get',
    });
  } catch (error) {
    console.log(error);
  }
}

function onPassChange({ password, hash, length }: ISecurePassword) {
  formValues.value.password = password;
  passObj.value = {
    password,
    hash,
    length: length || 0,
  };
  formRef.value?.validateField('password');
  if (length >= 6 && length <= 20) formRef.value?.validateField('rePassword');
}

function onRePassChange({ password, hash, length }: ISecurePassword) {
  formValues.value.rePassword = password;
  rePassObj.value = {
    password,
    hash,
    length: length || 0,
  };
  formRef.value?.validateField('rePassword');
  if (length >= 6 && length <= 20)formRef.value?.validateField('password');
}

function openService(e: MouseEvent) {
  e.stopPropagation();

  window.open('https://static.lianlianpay.com/accpweb/agreement/OpenAccountAgree_NOT_ACS.html');
}

watch(
  () => props.phone,
  async () => {
    if (props.phone) {
      formValues.value.phone = props.phone;
    }
  },
  { immediate: true },
);

defineExpose({
  validateForm,
  smsBtnRef,
  getRandomKeys: () => randomKeys.value,
  getPhone: () => formValues.value.phone,
  getValues: () => toRaw(formValues.value),
});

fetchRandomKeys();
</script>

<style scoped lang="scss" src="../index.scss" />
